/**
 * <FormError> — the single, canonical way to show a form-level error.
 *
 * Why this exists
 * ---------------
 * A user repeatedly pressed REGISTER on a phone and "nothing happened": the
 * validation error was rendered as a banner at the TOP of a long form, ~500px
 * above the button she had just tapped, so it was off-screen every time. Any
 * form error must be visible *where the user acted*, at every screen size.
 *
 * This component therefore does two things a plain <div> does not:
 *   1. announces itself to assistive tech (`role="alert" aria-live="assertive"`),
 *   2. scrolls itself into view and takes focus whenever a message appears.
 *
 * Place it directly ABOVE (or below) the submit button — not at the top of the
 * form — so it is adjacent to the control the user pressed.
 *
 * Usage
 * -----
 *
 *     const [error, setError] = useState('');
 *     const [errorKey, setErrorKey] = useState(0);
 *
 *     const onSubmit = async (e) => {
 *       e.preventDefault();
 *       const problem = validate();
 *       // Always bump the key on a failed submit — see the focusKey note below.
 *       setErrorKey((k) => k + 1);
 *       setError(problem ?? '');
 *     };
 *
 *     <FormError message={error} focusKey={errorKey} className="mb-3" />
 *     <button type="submit">REGISTER</button>
 *
 * The `focusKey` contract
 * -----------------------
 * React will NOT re-run an effect when state is re-set to an identical value,
 * so pressing a dead button twice with the same validation failure would
 * scroll/announce only the first time — which looks exactly like the bug this
 * component fixes. Callers therefore keep a counter that increments on EVERY
 * failed submit and pass it as `focusKey`; the effect is keyed on
 * `[message, focusKey]`, so each repeat press visibly re-announces.
 */

import React, { useEffect, useRef } from 'react';

/**
 * True when the OS/browser asks for reduced motion. Fully guarded: jsdom and
 * older browsers may have no `window` or no `matchMedia`, and this runs inside
 * an effect where a throw would break the render tree.
 */
function prefersReducedMotion() {
  try {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return false;
    }
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches === true;
  } catch (e) {
    return false;
  }
}

/**
 * @typedef {object} FormErrorProps
 * @property {string} [message]      — The error text. Renders nothing when falsy.
 * @property {number} [focusKey]     — Increment on every failed submit so a
 *                                      repeated identical error re-scrolls and
 *                                      re-announces. Default 0.
 * @property {string} [className]    — Appended last, so callers can add spacing.
 * @property {string} [id]           — Optional id, e.g. for aria-describedby on
 *                                      the submit button or the offending input.
 *
 * Any other prop (`data-testid`, `style`, …) is spread onto the container.
 * The role / aria-live / tabIndex / className contract is applied after the
 * spread and cannot be overridden.
 */

export function FormError({ message, focusKey = 0, className = '', id, ...rest }) {
  const ref = useRef(null);

  useEffect(() => {
    if (!message) return;
    const node = ref.current;
    if (!node) return;
    // Both guarded: jsdom has no scrollIntoView, and older Safari ignores the
    // focus options object. Neither may be allowed to throw inside an effect.
    if (typeof node.scrollIntoView === 'function') {
      node.scrollIntoView({ block: 'center', behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
    }
    if (typeof node.focus === 'function') {
      node.focus({ preventScroll: true });
    }
  }, [message, focusKey]);

  if (!message) return null;

  return (
    <div
      // Passthrough first, so the accessibility contract below (role,
      // aria-live, tabIndex, ref, className) always wins over a caller's
      // stray prop. `data-testid` and friends land here.
      {...rest}
      ref={ref}
      id={id}
      role="alert"
      aria-live="assertive"
      // tabIndex -1 makes the banner programmatically focusable without adding
      // it to the tab order.
      tabIndex={-1}
      className={`rounded-lg border border-red-300 bg-red-50 p-3 text-sm font-semibold text-red-600 break-words focus:outline-none ${className}`}
    >
      {message}
    </div>
  );
}
